// klaviyo-bridge.js
// Maps the Lumi profile to the EXISTING Lagoon Klaviyo schema (so Lumi
// profiles slot into the same cohorts/flows the current pillow quiz feeds).
//
// Schema kept identical to the quiz:
//   wake_up_position   single  "Side" | "Back" | "Stomach" | "Changes all the time"
//   density            single  "Soft & Cloud-like" | "Cushy & Cradling" | "Firm & Supportive" | "Not sure"
//   body_type          single  "Slender" | "Average" | "Broad"
//   height             single  "Lower loft" | "Medium loft" | "Higher loft" | "I'm not sure"
//   sleep_issues       multi   comma-joined display strings (see ISSUE_LABELS)
//   sleep_goals        multi   comma-joined display strings (see GOAL_LABELS)
//   result1_name       single  "The Otter" etc.
//   Pillowcase         single  "Cooling Flex" | "Silk Luxe" (when complement was accepted)
//   Entered Welcome Flow  "Yes"
//   Accepts Marketing  true
//
// On submit we also subscribe the profile to the "Cartful Solutions" list so
// the existing lifecycle flows fire. Set window.LAGOON_KLAVIYO_LIST_ID to
// the Cartful Solutions list ID.
//
// PRODUCTION WIRING:
//   window.LAGOON_KLAVIYO_PUBLIC_KEY = '<6-char site ID>';
//   window.LAGOON_KLAVIYO_LIST_ID    = '<Cartful Solutions list ID>';
//   // optional — preferred over public-key for production:
//   window.LAGOON_KLAVIYO_PROXY = 'https://your.backend/klaviyo-identify';
//
// LOCAL STORAGE KEYS:
//   lagoon.lumi.session.v1    full conversation + profile + recommendation
//   lagoon.lumi.email.v1      submitted email (so we don't re-prompt)
//   lagoon.lumi.synced.v1     {syncedAt, props, pending} sync receipt

window.LAGOON_KLAVIYO_PUBLIC_KEY = window.LAGOON_KLAVIYO_PUBLIC_KEY || null;
window.LAGOON_KLAVIYO_PROXY      = window.LAGOON_KLAVIYO_PROXY      || null;
window.LAGOON_KLAVIYO_LIST_ID    = window.LAGOON_KLAVIYO_LIST_ID    || null;
// The metric your welcome-series flow triggers on. MUST match the legacy
// quiz's event name EXACTLY (case-sensitive) or the flow won't fire.
window.LAGOON_KLAVIYO_SURVEY_METRIC = window.LAGOON_KLAVIYO_SURVEY_METRIC || 'Filled Out Survey';

// ─────────────────────────────────────────────────────────────
// Display-label maps — match the existing quiz's Klaviyo values exactly.
// ─────────────────────────────────────────────────────────────

const POSITION_LABELS = {
  side: 'Side',
  back: 'Back',
  stomach: 'Stomach',
  combo: 'Changes all the time',
};

const DENSITY_LABELS = {
  soft: 'Soft & Cloud-like',
  cushy: 'Cushy & Cradling',
  firm: 'Firm & Supportive',
  notsure: 'Not sure',
};

const BODY_LABELS = {
  slender: 'Slender',
  average: 'Average',
  broad: 'Broad',
};

const HEIGHT_LABELS = {
  low: 'Lower loft',
  medium: 'Medium loft',
  high: 'Higher loft',
  notsure: "I'm not sure",
};

const ISSUE_LABELS = {
  toss:     'I toss & turn too much',
  neck:     'I wake up with neck or shoulder pain',
  hot:      'I sleep hot & need cooling',
  snore:    'I snore or have trouble breathing',
  wrinkles: 'I worry about face wrinkles & pressure',
  none:     'No issues, just looking for an upgrade',
};

const GOAL_LABELS = {
  fall_asleep_faster: 'Fall asleep faster',
  deep_sleep:         'Get more deep & REM sleep',
  pain_free:          'Wake up pain-free',
  cool:               'Stay cool all night',
  upgrading:          'Just upgrading',
};

const PILLOW_LABELS = {
  otter:      'The Otter',
  otter2:     'The Otter II',   // NOTE: any Klaviyo flow/segment filtering
                                // result1_name must use "contains The Otter"
                                // rather than "equals" to catch both.
  fox:        'The Fox',
  chinchilla: 'The Chinchilla',
  puffin:     'The Puffin',
  polarbear:  'The Polar Bear',
};

const PILLOWCASE_LABELS = {
  'cooling-pillowcase': 'Cooling Flex',
  'silk-pillowcase':    'Silk Luxe',
};

// Build the Klaviyo property payload from a Lumi profile + recommendation.
// Skips empty values so we don't blow away existing properties with blanks.
function mapProfileToKlaviyo(p, recommendation, email) {
  // sleep_issues is multi-select. Internal model uses `concerns` (array) OR
  // `concern` (legacy single). Also: temp=hot maps into sleep_issues since
  // the existing schema doesn't have a separate temp field — that's how
  // hot-sleeper segmentation works today.
  const issues = new Set();
  if (Array.isArray(p.concerns)) p.concerns.forEach(c => ISSUE_LABELS[c] && issues.add(ISSUE_LABELS[c]));
  if (p.concern && ISSUE_LABELS[p.concern]) issues.add(ISSUE_LABELS[p.concern]);
  if (p.temp === 'hot' && !issues.has(ISSUE_LABELS.hot)) issues.add(ISSUE_LABELS.hot);

  // sleep_goals — already an array in the Lumi model
  const goals = Array.isArray(p.goals)
    ? p.goals.map(g => GOAL_LABELS[g]).filter(Boolean)
    : [];

  const out = {
    // Klaviyo reserved
    $email: email || p.email,
    $consent: ['email'],
    'Accepts Marketing': true,

    // EXISTING quiz schema — same keys and value formats
    wake_up_position: POSITION_LABELS[p.position] || null,
    density:          DENSITY_LABELS[p.density]   || null,
    body_type:        BODY_LABELS[p.body]         || null,
    height:           HEIGHT_LABELS[p.height]     || null,
    sleep_issues:     issues.size ? [...issues].join(',') : null,
    sleep_goals:      goals.length ? goals.join(',') : null,
    result1_name:     PILLOW_LABELS[recommendation?.pillow_id] || null,
    Pillowcase:       PILLOWCASE_LABELS[recommendation?.complement?.id] || null,
    'Entered Welcome Flow': 'Yes',

    // Lumi-specific properties — useful but namespaced so they don't
    // collide with existing reporting. Optional to consume.
    lumi_runner_up:              PILLOW_LABELS[recommendation?.runner_up_id] || null,
    lumi_recommended_confidence: recommendation?.confidence || null,
    lumi_buying_for:             p.buying_for || null,
    lumi_medical_flag:           p.medical_flag === true ? true : null,
    lumi_source:                 'lagoon-sleep-concierge',
    lumi_session_completed_at:   new Date().toISOString(),
  };

  // Strip empty/null so Klaviyo doesn't clear existing values
  Object.keys(out).forEach(k => {
    const v = out[k];
    if (v === null || v === undefined || v === '') delete out[k];
  });
  return out;
}

// Submit the profile to Klaviyo + subscribe to Cartful Solutions list.
// Prefers server proxy when configured; otherwise uses public client API.
window.lagoonSyncToKlaviyo = async function({ email, profile, recommendation }) {
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { ok: false, error: 'invalid-email' };
  }
  const props = mapProfileToKlaviyo(profile || {}, recommendation || {}, email);

  // Persist locally first so a network failure never loses the data
  try {
    localStorage.setItem('lagoon.lumi.email.v1', email);
    localStorage.setItem('lagoon.lumi.synced.v1', JSON.stringify({
      syncedAt: Date.now(), props, pending: true,
    }));
  } catch (e) {}

  // 1) Preferred: server proxy. Your backend uses the Klaviyo private key
  //    to identify + subscribe. Single endpoint, atomic.
  if (window.LAGOON_KLAVIYO_PROXY) {
    try {
      const res = await fetch(window.LAGOON_KLAVIYO_PROXY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          properties: props,
          list_id: window.LAGOON_KLAVIYO_LIST_ID,
          // Tell the backend to fire the survey metric that triggers the
          // welcome flow. (Your proxy must emit this as a Klaviyo event.)
          event: window.LAGOON_KLAVIYO_SURVEY_METRIC,
        }),
      });
      if (!res.ok) throw new Error('proxy-' + res.status);
      markSynced(props);
      return { ok: true, via: 'proxy' };
    } catch (e) {
      console.warn('[lumi] proxy failed, falling back to client API', e);
    }
  }

  // 2) Fallback: public client API (no private key needed). Two calls —
  //    update profile properties, then subscribe to the list.
  if (window.LAGOON_KLAVIYO_PUBLIC_KEY) {
    try {
      await klaviyoUpdateProfile(email, props);
      if (window.LAGOON_KLAVIYO_LIST_ID) {
        await klaviyoSubscribeToList(email, window.LAGOON_KLAVIYO_LIST_ID);
      }
      // Fire the metric that triggers the welcome-series flow. This is the
      // piece that was missing — subscribing to a list / setting a property
      // does NOT trigger an event-based flow.
      await klaviyoTrackEvent(email, window.LAGOON_KLAVIYO_SURVEY_METRIC, props);
      markSynced(props);
      return { ok: true, via: 'client-api' };
    } catch (e) {
      console.error('[lumi] client API failed', e);
      return { ok: false, error: String(e) };
    }
  }

  // 3) Demo mode — log the payload so the UX flow can be reviewed without
  //    live Klaviyo keys. Local storage still has it; production picks up
  //    on next visit when keys are wired.
  console.info('[lumi DEMO] Klaviyo payload:', { email, properties: props, event: window.LAGOON_KLAVIYO_SURVEY_METRIC, list: window.LAGOON_KLAVIYO_LIST_ID || '(not set — would subscribe to Cartful Solutions)' });
  markSynced(props);
  return { ok: true, via: 'demo' };
};

async function klaviyoUpdateProfile(email, properties) {
  const customProps = Object.fromEntries(
    Object.entries(properties).filter(([k]) => !k.startsWith('$') && k !== 'Accepts Marketing')
  );
  const payload = {
    data: {
      type: 'profile',
      attributes: {
        email,
        properties: customProps,
      },
    },
  };
  const url = `https://a.klaviyo.com/client/profiles/?company_id=${window.LAGOON_KLAVIYO_PUBLIC_KEY}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'revision': '2024-10-15',
    },
    body: JSON.stringify(payload),
  });
  // 409 is "profile already exists" — fine, properties merge.
  if (!res.ok && res.status !== 409) throw new Error('profile-' + res.status);
}

async function klaviyoSubscribeToList(email, listId) {
  const payload = {
    data: {
      type: 'subscription',
      attributes: {
        custom_source: 'Lagoon Sleep Concierge',
        profile: {
          data: {
            type: 'profile',
            attributes: { email },
          },
        },
      },
      relationships: {
        list: { data: { type: 'list', id: listId } },
      },
    },
  };
  const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${window.LAGOON_KLAVIYO_PUBLIC_KEY}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'revision': '2024-10-15',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok && res.status !== 202) throw new Error('subscribe-' + res.status);
}

// Fire a Klaviyo metric/event via the public client API. This is what an
// event-triggered flow (e.g. the welcome series) listens for — a profile
// property or list subscription will NOT start it.
async function klaviyoTrackEvent(email, metricName, props) {
  // Keep event properties light + non-reserved (no $email/$consent here).
  const eventProps = Object.fromEntries(
    Object.entries(props || {}).filter(([k]) => !k.startsWith('$') && k !== 'Accepts Marketing')
  );
  const payload = {
    data: {
      type: 'event',
      attributes: {
        properties: eventProps,
        metric: {
          data: { type: 'metric', attributes: { name: metricName } },
        },
        profile: {
          data: { type: 'profile', attributes: { email } },
        },
      },
    },
  };
  const url = `https://a.klaviyo.com/client/events/?company_id=${window.LAGOON_KLAVIYO_PUBLIC_KEY}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'revision': '2024-10-15',
    },
    body: JSON.stringify(payload),
  });
  if (!res.ok && res.status !== 202) throw new Error('event-' + res.status);
}

function markSynced(props) {
  try {
    localStorage.setItem('lagoon.lumi.synced.v1', JSON.stringify({
      syncedAt: Date.now(), props, pending: false,
    }));
  } catch (e) {}
}

window.lagoonGetSavedEmail = function() {
  try { return localStorage.getItem('lagoon.lumi.email.v1') || null; }
  catch (e) { return null; }
};

window.lagoonClearKlaviyoState = function() {
  try {
    localStorage.removeItem('lagoon.lumi.email.v1');
    localStorage.removeItem('lagoon.lumi.synced.v1');
  } catch (e) {}
};

// Expose the label maps so the strategy artboard can render them
window.LAGOON_KLAVIYO_LABELS = {
  position: POSITION_LABELS,
  density:  DENSITY_LABELS,
  body:     BODY_LABELS,
  height:   HEIGHT_LABELS,
  issues:   ISSUE_LABELS,
  goals:    GOAL_LABELS,
  pillow:   PILLOW_LABELS,
  pillowcase: PILLOWCASE_LABELS,
};
